import { Button, Checkbox, Col, Modal, Row, Typography } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import InputBox from "../../../components/InputBox";
import SelectBox from "../../../components/SelectBox";
import apiService from "../../../services/apiService";
import styles from "../manageRole.module.css";
import { ShieldCheck } from "lucide-react";

interface Props {
  openEditModal: boolean;
  closeModal: () => void;
  isEdit?: boolean;
  id?: string | number;
  refreshList?: () => void;
}

interface TeamRole {
  id: number;
  role_name: string;
}

interface Team {
  id: number;
  team_name: string;
  createdAt: string;
  updatedAt: string;
  roles: TeamRole[];
}

interface TeamsResponse {
  message: string;
  teams: Team[];
}

const defaultPermissions = [
  { key: "1", title: "Manage Lead", permissionKey: "manage_lead" },
  { key: "2", title: "Manage Role", permissionKey: "manage_roles" },
  { key: "3", title: "Manage Customer", permissionKey: "manage_customer" },
  { key: "4", title: "Manage Partner", permissionKey: "manage_partner" },
  { key: "5", title: "Manage Lender", permissionKey: "manage_lender" },
  { key: "6", title: "Term Sheet", permissionKey: "manage_term_sheet" },
  { key: "7", title: "Rental Calculation", permissionKey: "manage_calculator" },
  { key: "8", title: "Manage Login", permissionKey: "manage_login" },
  { key: "9", title: "Manage Supplier", permissionKey: "manage_supplier" },
  { key: "10", title: "Manage Purchase Order", permissionKey: "manage_purchase_order" },
  { key: "11", title: "Manage Rental Schedule", permissionKey: "manage_rental_schedule" },
  { key: "12", title: "Manage Revenue", permissionKey: "manage_revenue" },
  { key: "13", title: "Manage Rental Invoice", permissionKey: "manage_rental_invoice" },
  { key: "14", title: "Manage Post Disbursement", permissionKey: "manage_post_disbursement" },
  { key: "15", title: "Manage Portfolio", permissionKey: "manage_portfolio" },
  { key: "16", title: "Admin Dashboard", permissionKey: "manage_admin_dashboard" },
];

const RolePermissionModal = ({
  openEditModal,
  closeModal,
  isEdit = false,
  id = "",
  refreshList,
}: Props) => {
  const [roleName, setRoleName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [permissionsState, setPermissionsState] = useState<any>(() => {
    const initialState: any = {};
    defaultPermissions.forEach((p) => {
      initialState[p.permissionKey] = {
        view: "",
        update: "",
        assign: "",
        delete: "",
      };
    });
    return initialState;
  });

  const fetchTeams = async () => {
    try {
      const response: TeamsResponse = await apiService.get("/users/teams");
      if (response.teams) setTeams(response.teams);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const fetchRoleById = async (id: string | number) => {
    try {
      const response: any = await apiService.get(`/roles/${id}`);
      if (response) {
        setRoleName(response.role_name);
        const matchedTeam = teams.find((t) => t.team_name === response.team_name);
        setSelectedTeam(matchedTeam ? matchedTeam.id : null);
        
        const apiPermissions = response.permissions || {};
        const newState: any = {};
        defaultPermissions.forEach(p => {
          newState[p.permissionKey] = apiPermissions[p.permissionKey] || { view: "", update: "", assign: "", delete: "" };
        });
        setPermissionsState(newState);
      }
    } catch (error) {
      console.error("Error fetching role:", error);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (openEditModal && isEdit && id && teams.length > 0) {
      fetchRoleById(id);
    } else if (!isEdit) {
      resetForm();
    }
  }, [openEditModal, isEdit, id, teams]);

  const resetForm = () => {
    setRoleName("");
    setSelectedTeam(null);
    const resetState: any = {};
    defaultPermissions.forEach((p) => {
      resetState[p.permissionKey] = { view: "", update: "", assign: "", delete: "" };
    });
    setPermissionsState(resetState);
  };

  const handleToggle = (permissionKey: string, type: string, value: string) => {
    setPermissionsState((prev: any) => ({
      ...prev,
      [permissionKey]: {
        ...prev[permissionKey],
        [type]: prev[permissionKey][type] === value ? "" : value,
      },
    }));
  };

  const handleSelectAll = (checked: boolean) => {
    const newState: any = {};
    defaultPermissions.forEach(p => {
      newState[p.permissionKey] = checked ? { view: "all", update: "all", assign: "all", delete: "all" } : { view: "", update: "", assign: "", delete: "" };
    });
    setPermissionsState(newState);
  };

  const handleColumnToggle = (type: string, checked: boolean) => {
    setPermissionsState((prev: any) => {
      const newState = { ...prev };
      defaultPermissions.forEach(p => {
        newState[p.permissionKey] = {
          ...newState[p.permissionKey],
          [type]: checked ? "all" : ""
        };
      });
      return newState;
    });
  };

  const handleApply = async () => {
    if (!roleName) { toast.error("Please enter role name."); return; }
    if (!selectedTeam) { toast.error("Please select a team."); return; }

    const payload = {
      roleName,
      team: String(selectedTeam),
      permissions: permissionsState,
    };

    try {
      if (isEdit) await apiService.put(`/roles/${id}`, payload);
      else await apiService.post("/roles/create", payload);
      
      toast.success(isEdit ? "Role updated successfully" : "Role created successfully");
      refreshList?.();
      closeModal();
    } catch (error: any) {
      toast.error(error.message || "Something went wrong");
    }
  };

  const col1 = defaultPermissions.slice(0, 7);
  const col2 = defaultPermissions.slice(7);

  const renderPermissionCard = (items: typeof defaultPermissions) => {
    const types = ["view", "update", "assign", "delete"];
    return (
      <div className={styles.permissionCard}>
        <div className={styles.permissionHeader}>
          <div className={styles.permissionHeaderTitle}>Permission</div>
          {types.map(type => (
            <div key={type} className={styles.permissionHeaderCol}>
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[13px] font-bold text-[#1e293b]">{type.charAt(0).toUpperCase()}</span>
                    <Checkbox 
                        onChange={(e) => handleColumnToggle(type, e.target.checked)}
                        style={{ transform: 'scale(0.85)' }}
                    />
                </div>
            </div>
          ))}
        </div>
        {items.map((item) => (
          <div key={item.key} className={styles.permissionRow}>
            <div className={styles.permissionTitle}>
              {item.title}
            </div>
            {types.map((type) => (
              <div key={type} className={styles.permissionControls}>
                <div className={styles.subOptions}>
                  <div className={styles.subOptionItem} onClick={() => handleToggle(item.permissionKey, type, "all")}>
                    <Checkbox checked={permissionsState[item.permissionKey][type] === "all"} /> All
                  </div>
                  <div className={styles.subOptionItem} onClick={() => handleToggle(item.permissionKey, type, "assigned")}>
                    <Checkbox checked={permissionsState[item.permissionKey][type] === "assigned"} /> Assigned
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      open={openEditModal}
      onCancel={closeModal}
      width={1000}
      footer={null}
      centered
      styles={{
        mask: { backdropFilter: 'blur(4px)' },
        content: { borderRadius: '24px', padding: '24px' }
      }}
      title={
        <div className={styles.modalHeader}>
            <div className="w-10 h-10 rounded-xl bg-[#3f8b98] flex items-center justify-center shadow-lg shadow-[#3f8b98]/20">
                <ShieldCheck size={20} color="#fff" />
            </div>
            <Typography.Title level={4} style={{ margin: 0, fontWeight: 700 }}>Role Permission</Typography.Title>
        </div>
      }
    >
      <div className={styles.scrollableBody}>
        <Row gutter={24} style={{ marginBottom: '24px' }}>
          <Col span={12}>
            <InputBox
              label="Role Name"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Enter role name"
            />
          </Col>
          <Col span={12}>
            <SelectBox
              label="Team Name"
              value={selectedTeam || undefined}
              onChange={setSelectedTeam}
              placeholder="Select team name"
              options={teams.map(t => ({ value: t.id, label: t.team_name }))}
            />
          </Col>
        </Row>

        <div className={styles.selectAllWrap}>
            <Checkbox 
                checked={Object.values(permissionsState).every((p: any) => Object.values(p).every(v => !!v))}
                onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <div className={styles.selectAllLabel}>Select All</div>
            <div className={styles.selectAllHint}>Select all permissions for this role</div>
            
            <div className={styles.legendSection} style={{ marginLeft: 'auto', gap: '8px' }}>
                <div className={`${styles.legendBadge} ${styles.viewBadge}`}>V = View</div>
                <div className={`${styles.legendBadge} ${styles.updateBadge}`}>U = Update</div>
                <div className={`${styles.legendBadge} ${styles.assignBadge}`}>A = Assign</div>
                <div className={`${styles.legendBadge} ${styles.deleteBadge}`}>D = Delete</div>
            </div>
        </div>

        <div className={styles.permissionGrid}>
          {renderPermissionCard(col1)}
          {renderPermissionCard(col2)}
        </div>

        <div style={{ 
          marginTop: "24px", 
          paddingTop: "20px", 
          borderTop: "1px solid #f1f5f9", 
          display: "flex", 
          justifyContent: "flex-end", 
          gap: "12px" 
        }}>
          <Button 
            onClick={closeModal}
            style={{ 
                height: '44px', 
                borderRadius: '12px', 
                paddingInline: '32px', 
                fontSize: '14px', 
                fontWeight: 600,
                color: '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            onClick={handleApply}
            style={{ 
                height: '44px', 
                borderRadius: '12px', 
                paddingInline: '40px', 
                backgroundColor: '#3f8b98', 
                boxShadow: '0 8px 20px rgba(63, 139, 152, 0.25)',
                fontSize: '14px',
                fontWeight: 700,
                border: 'none'
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RolePermissionModal;
